import React, {useEffect, useId, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {isOk} from '../../../utils/apiResponse';
import {copyPlainText} from '../../../utils/copyPlainText';
import {FACEBOOK_CHANNEL, GOOGLE_CHANNEL} from '../../channels/channels';
import {committedFilters, fetchGa4DimensionValues, fetchGa4FiltersPreview} from './ga4FiltersPreviewApi';

/**
 * Vista previa en vivo de lo que capturan los Filtros GA4 de una cuenta, mientras se arman.
 *
 * Antes el editor no mostraba nada: un filtro mal armado se descubría recién en el Tablero, después
 * de la sync. Así quedaron 15 líneas en ~0 con «Fuente contiene cpc» (querían Medio), y Fli Fla Flu
 * (25/09) fue a GA4 a comparar valores a mano porque no sabía si «Medio contiene cp» tomaba lo que
 * quería (tomaba cpc y cpa). Ahora se ve cuánto entra, qué combinaciones entran y, si no entra nada,
 * cuáles son los valores reales y si un valor está puesto en el campo equivocado.
 *
 * Nunca bloquea el guardado: si GA4 no responde, lo dice en una línea y el editor sigue igual.
 *
 * Con la cuenta de pauta de la fila (`adAccountId`), dice además cuánto de lo filtrado se puede
 * asignar a una campaña de esa cuenta, y cómo etiquetar los anuncios si falta (Hesta, 23/09: 30 de
 * 41 compras de Meta llegaban sin etiqueta y nadie se enteraba hasta armar un informe).
 */

export const PREVIEW_DEBOUNCE_MS = 700;
const HINT_FIELDS = ['SOURCE', 'MEDIUM'];
const TOP_VALUES_SHOWN = 5;
const EXAMPLE_MAX_CHARS = 40;
// Los parámetros dinámicos de Meta. Es un literal para copiar, no copy: no se traduce, así que no va
// por `t()`. Empieza con `&` porque va al final de lo que el anuncio ya tenga (`utm_source=…`); sin
// el separador quedaba `utm_medium=cpcutm_id=…` (C-ola-2-asociar-ga4-sugerencias-04). En un campo
// vacío, el `&` de más es inocuo.
export const META_URL_PARAMETERS = '&utm_id={{campaign.id}}&utm_campaign={{campaign.name}}';

export function useGa4FiltersPreview(propertyId, filters, adAccountId) {
    const signature = JSON.stringify(committedFilters(filters));
    const requestId = useRef(0);
    const lastPropertyId = useRef(propertyId);
    const [state, setState] = useState({loading: false, data: null, errorCode: null});
    // «Volver a calcular» después de un error: la misma consulta, sin tocar ningún chip.
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!propertyId) {
            requestId.current += 1;
            setState({loading: false, data: null, errorCode: null});
            return undefined;
        }
        const id = requestId.current + 1;
        requestId.current = id;
        const propertyChanged = lastPropertyId.current !== propertyId;
        lastPropertyId.current = propertyId;
        // Con otro chip se conserva el resultado anterior mientras carga (atenuado): saltar a un
        // spinner vacío en cada chip hace que la caja «parpadee». Con OTRA property no: serían los
        // números de la anterior bajo el nombre de la nueva.
        setState(prev => (propertyChanged ? {loading: true, data: null, errorCode: null} : {...prev, loading: true}));
        const timer = setTimeout(() => {
            fetchGa4FiltersPreview(propertyId, JSON.parse(signature), adAccountId)
                .then(response => {
                    if (requestId.current !== id) return;
                    if (isOk(response) && response.data) {
                        setState({loading: false, data: response.data, errorCode: null});
                    } else {
                        setState({loading: false, data: null, errorCode: (response && response.status) || 'ERROR'});
                    }
                })
                .catch(() => {
                    if (requestId.current !== id) return;
                    setState({loading: false, data: null, errorCode: 'NETWORK_ERROR'});
                });
        }, PREVIEW_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [propertyId, signature, attempt, adAccountId]);

    return {...state, retry: () => setAttempt(n => n + 1)};
}

// es-AR agrupa desde 4 cifras («1.336»); `es` a secas no («1336»), y en la misma caja convivían los
// dos formatos (QA-VIS-ga4prev-05).
const numberLocale = (language) => (String(language || 'es').startsWith('en') ? 'en-US' : 'es-AR');
// Espacio no separable: «(100 %» y «2 compras» no se parten al final de la línea (QA-VIS-ga4prev-15).
const NBSP = ' ';

function valueMatches(filterValue, candidate, operator) {
    const a = String(filterValue).toLowerCase();
    const b = String(candidate).toLowerCase();
    return operator === 'CONTAINS' ? b.includes(a) : a === b;
}

/**
 * «cpc» puesto en Fuente: el valor no aparece entre los de su campo pero sí entre los del otro.
 * Es el error que dejó 15 líneas en cero. Sólo se mira Fuente↔Medio, que es donde pasa, y nunca
 * con regex (no se puede decidir sin evaluarla).
 */
export function crossFieldHints(filters, valuesByField) {
    const hints = [];
    committedFilters(filters).forEach(f => {
        if (!HINT_FIELDS.includes(f.field) || f.operator === 'REGEXP_PARTIAL_MATCH') return;
        const own = valuesByField[f.field];
        if (!own) return;
        f.values.forEach(value => {
            if (own.some(c => valueMatches(value, c.value, f.operator))) return;
            const other = HINT_FIELDS.find(field => field !== f.field
                && (valuesByField[field] || []).some(c => valueMatches(value, c.value, f.operator)));
            if (other) hints.push({value, field: f.field, suggestedField: other});
        });
    });
    return hints;
}

function useValuesForZeroState(propertyId, fields, active) {
    const [valuesByField, setValuesByField] = useState({});
    const key = fields.join(',');
    useEffect(() => {
        // Lo de otra property (o de otros campos) no vale: se descarta antes de pedir lo nuevo.
        setValuesByField({});
        if (!active || !propertyId) return undefined;
        let cancelled = false;
        Promise.all(fields.map(field => fetchGa4DimensionValues(propertyId, field).then(values => [field, values])))
            .then(pairs => {
                if (!cancelled) setValuesByField(Object.fromEntries(pairs));
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyId, key, active]);
    return valuesByField;
}

/**
 * `muted`: el editor ya muestra un aviso ámbar (dos filtros sobre el mismo campo). Una segunda caja
 * ámbar pegada diciendo algo parecido compite con el primero: la caja en cero queda neutra y sólo
 * suma la pista (QA-VIS-ga4prev-14).
 *
 * `adAccountId` / `channel`: la cuenta de pauta de la fila. Sin ellos (la GA4 propia de una línea)
 * no hay cobertura por campaña y la caja es la de siempre.
 */
export function GA4FiltersPreview({propertyId, filters, muted = false, adAccountId = null, channel = null}) {
    const {t, i18n} = useTranslation();
    const {loading, data, errorCode, retry} = useGa4FiltersPreview(propertyId, filters, adAccountId);

    const mainMetric = data && data.metrics ? data.metrics[0] : null;
    const total = data && mainMetric ? (data.totals[mainMetric] || 0) : null;
    const isZero = data !== null && total === 0;
    const usedFields = [...new Set([...HINT_FIELDS, ...committedFilters(filters).map(f => f.field)])]
        .filter(f => !String(f).startsWith('PRODUCT_'));
    const valuesByField = useValuesForZeroState(propertyId, usedFields, isZero);

    if (!propertyId) return null;

    const locale = numberLocale(i18n.language);
    const number = (n) => Math.round(n || 0).toLocaleString(locale);
    const noEscape = {interpolation: {escapeValue: false}};

    // El encabezado se ve en TODOS los estados (también cargando y con error): sin él, una frase gris
    // suelta no dice de qué habla (QA-VIS-ga4prev-22).
    const header = (days) => (
        <div className="ga4-filters-preview-header">
            <span className="ga4-filters-preview-title">{t('ga4_filters.preview_title')}</span>
            <span className="ga4-filters-preview-badge">{t('ga4_filters.live_badge')}</span>
            <span className="ga4-filters-preview-period">{t('ga4_filters.preview_period', {count: days})}</span>
            {loading && <Spinner color="positive small"/>}
        </div>
    );

    if (!data && loading) {
        return (
            <div className="ga4-filters-preview -loading" aria-live="polite" aria-busy="true">
                {header(30)}
                <div className="ga4-filters-preview-note">{t('ga4_filters.preview_loading')}</div>
            </div>
        );
    }

    if (!data && errorCode) {
        const key = errorCode === 'GA4_PERMISSION_DENIED'
            ? 'ga4_filters.preview_error_permission'
            : 'ga4_filters.preview_error';
        return (
            <div className="ga4-filters-preview -error" role="status">
                {header(30)}
                <div className="ga4-filters-preview-note">
                    {t(key)}{' '}
                    {errorCode !== 'GA4_PERMISSION_DENIED' && (
                        <button type="button" className="ga4-filters-preview-retry" onClick={retry}>
                            {t('ga4_filters.preview_retry')}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (!data) return null;

    const isItem = data.scope === 'item';
    const propertyTotal = data.property_totals ? (data.property_totals[mainMetric] || 0) : 0;
    // Tope en 100: la parte no puede superar al todo, aunque GA4 redondee distinto las dos consultas.
    const share = propertyTotal > 0 ? Math.min((total / propertyTotal) * 100, 100) : null;
    const maxRow = data.rows.reduce((max, r) => Math.max(max, r.metrics[mainMetric] || 0), 0);
    const zeroClass = isZero ? (muted ? ' -zero -muted' : ' -zero') : '';

    return (
        <div className={`ga4-filters-preview${loading ? ' -stale' : ''}${zeroClass}`}
             aria-live="polite" aria-busy={loading}>
            {header(data.period ? data.period.days : 30)}

            {isZero ? (
                <ZeroState t={t} filters={filters} valuesByField={valuesByField} noEscape={noEscape}/>
            ) : (
                <>
                    <div className="ga4-filters-preview-summary">
                        {summaryParts(t, data, number).map((part, i) => (
                            <React.Fragment key={i}>{i > 0 && ' · '}<strong>{part}</strong></React.Fragment>
                        ))}
                        {share !== null && (
                            <span className="ga4-filters-preview-share">
                                {' '}{t(isItem ? 'ga4_filters.preview_share_item' : 'ga4_filters.preview_share',
                                    {percent: formatPercent(share, locale)})}
                            </span>
                        )}
                    </div>
                    {isItem && <div className="ga4-filters-preview-note">{t('ga4_filters.preview_scope_item_note')}</div>}
                    {data.rows.length > 0 && (
                        <ul className="ga4-filters-preview-rows" aria-label={t('ga4_filters.preview_rows_title')}>
                            {data.rows.map((row, i) => {
                                const label = data.fields.map(f => row.values[f] || '—').join(' / ');
                                return (
                                    <li key={i} className="ga4-filters-preview-row">
                                        <span className="ga4-filters-preview-row-label" title={label}>{label}</span>
                                        <span className="ga4-filters-preview-row-bar" aria-hidden="true">
                                            <span style={{width: `${maxRow > 0 ? (row.metrics[mainMetric] / maxRow) * 100 : 0}%`}}/>
                                        </span>
                                        <span className="ga4-filters-preview-row-value">{number(row.metrics[mainMetric])}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {data.rows_truncated && <div className="ga4-filters-preview-note">{t('ga4_filters.preview_rows_more')}</div>}
                    {/* Sin filtros, la cobertura sería la de la property entera: lo directo y lo
                        orgánico saldrían como «falta etiquetar». El backend ya no la manda; esto es
                        la red (C-ola-2-asociar-ga4-sugerencias-01). */}
                    {data.coverage && data.coverage.total > 0 && committedFilters(filters).length > 0 && (
                        <Coverage t={t} coverage={data.coverage} channel={channel} number={number}
                                  isSessions={data.metrics && data.coverage.metric === data.metrics[0]}/>
                    )}
                </>
            )}
        </div>
    );
}

// Por caracteres y no por unidades UTF-16: un corte en medio de un emoji dejaba «�».
const shorten = (text) => {
    const chars = Array.from(String(text));
    return chars.length > EXAMPLE_MAX_CHARS ? `${chars.slice(0, EXAMPLE_MAX_CHARS - 1).join('')}…` : chars.join('');
};

// Las partes de lo que no se asigna, en el orden en que se leen. Cada una se arregla distinto, y la
// que casi siempre pesa (medido el 11/10 en las líneas de Meta con poca cobertura) es la última: un
// filtro que toma otra cuenta, o etiquetados propios. Sólo los nombres dicen cuál de los dos es.
const MISSING_PARTS = [
    ['untagged', 'ga4_filters.coverage.missing'],
    ['not_ads', 'ga4_filters.coverage.not_ads'],
    ['ambiguous', 'ga4_filters.coverage.ambiguous'],
    ['archived', 'ga4_filters.coverage.archived'],
];

/**
 * Cuánto de lo filtrado se puede asignar a una campaña de la cuenta de la fila, y lo que no, partido
 * según cómo se arregla. «Cómo etiquetar» aparece sólo si etiquetar arregla algo: no para lo que no
 * viene de anuncios ni para lo de campañas archivadas.
 */
function Coverage({t, coverage, channel, number, isSessions}) {
    const howId = useId();
    const [howOpen, setHowOpen] = useState(false);
    const [copyState, setCopyState] = useState(null);
    const noEscape = {interpolation: {escapeValue: false}};
    const fullExamples = coverage.unmatched_examples || [];
    const examples = fullExamples.map(shorten);
    const taggingHelps = coverage.untagged > 0 || coverage.unmatched > 0 || coverage.ambiguous > 0;
    const hasGuide = channel === FACEBOOK_CHANNEL || channel === GOOGLE_CHANNEL;

    const copy = () => copyPlainText(META_URL_PARAMETERS)
        .then(() => setCopyState('copied'))
        .catch(() => setCopyState('error'));

    return (
        <div className="ga4-filters-preview-coverage">
            <div className="ga4-filters-preview-coverage-line">
                {t(isSessions ? 'ga4_filters.coverage.line_sessions' : 'ga4_filters.coverage.line',
                    {count: coverage.total, total: number(coverage.total), assigned: number(coverage.assigned)})}
            </div>
            {MISSING_PARTS.filter(([part]) => coverage[part] > 0).map(([part, key]) => (
                <div key={part} className="ga4-filters-preview-note">
                    {t(key, {count: coverage[part], formatted: number(coverage[part])})}
                </div>
            ))}
            {coverage.unmatched > 0 && (
                // El `title` lleva los nombres enteros: en la frase van recortados.
                <div className="ga4-filters-preview-note ga4-filters-preview-coverage-unmatched"
                     title={fullExamples.length > 0 ? fullExamples.join(', ') : undefined}>
                    {t('ga4_filters.coverage.unmatched', {count: coverage.unmatched, formatted: number(coverage.unmatched)})}
                    {examples.length > 0 && (
                        <>{' '}{t('ga4_filters.coverage.unmatched_examples', {examples: examples.join(', '), ...noEscape})}</>
                    )}
                    {' '}{t('ga4_filters.coverage.unmatched_hint')}
                </div>
            )}
            {taggingHelps && hasGuide && (
                <>
                    <button type="button" className="ga4-filters-preview-coverage-how" aria-expanded={howOpen}
                            aria-controls={howId} onClick={() => setHowOpen(open => !open)}>
                        <FaIcon iconName={howOpen ? 'chevron-down' : 'chevron-right'}/>
                        <span>{t('ga4_filters.coverage.how')}</span>
                    </button>
                    {/* Siempre montada (con `hidden`): cerrada, `aria-controls` no apuntaba a nada. */}
                    <div id={howId} className="ga4-filters-preview-coverage-guide" hidden={!howOpen}>
                        {channel === FACEBOOK_CHANNEL ? (
                            <>
                                <div>{t('ga4_filters.coverage.meta_param')}</div>
                                <div className="ga4-filters-preview-coverage-code">
                                    <code>{META_URL_PARAMETERS}</code>
                                    <button type="button" className="ga4-filters-preview-coverage-copy" onClick={copy}>
                                        {t(copyState === 'copied' ? 'ga4_filters.coverage.copied' : 'ga4_filters.coverage.copy')}
                                    </button>
                                </div>
                                {copyState === 'error' && (
                                    <div className="ga4-filters-preview-note" role="status">
                                        {t('ga4_filters.coverage.copy_error')}
                                    </div>
                                )}
                                <div className="ga4-filters-preview-note">{t('ga4_filters.coverage.meta_source_note')}</div>
                            </>
                        ) : (
                            <div>{t('ga4_filters.coverage.google_autotag')}</div>
                        )}
                        <div className="ga4-filters-preview-note">{t('ga4_filters.coverage.how_note')}</div>
                    </div>
                </>
            )}
        </div>
    );
}

function summaryParts(t, data, number) {
    const [first, second, third] = data.metrics;
    const totals = data.totals;
    if (data.scope === 'item') {
        return [
            t('ga4_filters.preview_items', {count: Math.round(totals[first] || 0), formatted: number(totals[first])}),
            data.currency
                ? t('ga4_filters.preview_item_revenue_with_currency',
                    {formatted: number(totals[second]), currency: data.currency})
                : t('ga4_filters.preview_item_revenue', {formatted: number(totals[second])}),
        ];
    }
    return [
        t('ga4_filters.preview_sessions', {count: Math.round(totals[first] || 0), formatted: number(totals[first])}),
        t('ga4_filters.preview_conversions', {count: Math.round(totals[second] || 0), formatted: number(totals[second])}),
        t('ga4_filters.preview_purchases', {count: Math.round(totals[third] || 0), formatted: number(totals[third])}),
    ];
}

function formatPercent(value, locale) {
    const digits = value < 10 ? 1 : 0;
    return `${value.toLocaleString(locale, {maximumFractionDigits: digits, minimumFractionDigits: 0})}${NBSP}%`;
}

function ZeroState({t, filters, valuesByField, noEscape}) {
    const hints = crossFieldHints(filters, valuesByField);
    const fieldsWithValues = Object.keys(valuesByField).filter(f => (valuesByField[f] || []).length > 0);
    return (
        <div className="ga4-filters-preview-zero" role="status">
            <div className="ga4-filters-preview-zero-title">
                <FaIcon iconName="exclamation-triangle"/>
                <span>{t('ga4_filters.preview_zero_title')}</span>
            </div>
            {hints.map(h => (
                <div key={`${h.field}-${h.value}`} className="ga4-filters-preview-hint">
                    {t('ga4_filters.preview_cross_field_hint', {
                        value: h.value,
                        field: t(`ga4_filters.field_${h.field.toLowerCase()}`),
                        suggested: t(`ga4_filters.field_${h.suggestedField.toLowerCase()}`),
                        ...noEscape,
                    })}
                </div>
            ))}
            {fieldsWithValues.length > 0 && (
                <>
                    <div className="ga4-filters-preview-note">{t('ga4_filters.preview_zero_body')}</div>
                    <ul className="ga4-filters-preview-top-values">
                        {fieldsWithValues.map(f => (
                            <li key={f}>
                                <strong>{t(`ga4_filters.field_${f.toLowerCase()}`)}:</strong>{' '}
                                {valuesByField[f].slice(0, TOP_VALUES_SHOWN).map(v => v.value).join(', ')}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}
